import type {
  Article,
  ArticleTranslation,
} from "../data/articles";
import { getArticleImage } from "./articleImages";
import { getLocaleFromPathname } from "./localeRoutes";

const DEFAULT_ARTICLE_IMAGE = "https://dociohealth.com/og-article.png";
const PUBLISHER_LOGO =
  "https://dociohealth.com/assets/a17cbcaa-6f50-48e1-b3ea-6083f04328fa.png";

export interface ArticleSchema {
  "@context": "https://schema.org";
  "@type": "Article";
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  image: string;
  mainEntityOfPage: string;
  author: {
    "@type": "Organization";
    name: "Docio Health";
    url: "https://dociohealth.com/about";
  };
  publisher: {
    "@type": "Organization";
    name: "Docio Health";
    logo: {
      "@type": "ImageObject";
      url: string;
    };
  };
}

export function buildArticleSchema(
  article: Pick<Article, "slug" | "date" | "updatedAt" | "ogImage">,
  translation: ArticleTranslation,
  canonicalUrl: string,
): ArticleSchema {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: translation.title,
    description: translation.metaDescription,
    datePublished: article.date,
    dateModified: article.updatedAt ?? article.date,
    image: getArticleImage(article.slug, getLocaleFromPathname(new URL(canonicalUrl).pathname))?.url
      ?? article.ogImage ?? DEFAULT_ARTICLE_IMAGE,
    mainEntityOfPage: canonicalUrl,
    author: {
      "@type": "Organization",
      name: "Docio Health",
      url: "https://dociohealth.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "Docio Health",
      logo: {
        "@type": "ImageObject",
        url: PUBLISHER_LOGO,
      },
    },
  };
}

type RelatedArticle = Pick<Article, "slug" | "audience" | "keywords" | "date">;

function normalizedKeywords(article: RelatedArticle): Set<string> {
  return new Set(
    article.keywords.map((keyword) => keyword.trim().toLocaleLowerCase()),
  );
}

export function getRelatedArticles<T extends RelatedArticle>(
  article: RelatedArticle,
  candidates: readonly T[],
  limit = 3,
): T[] {
  const sourceKeywords = normalizedKeywords(article);

  return candidates
    .filter(
      (candidate) =>
        candidate.slug !== article.slug &&
        candidate.audience === article.audience,
    )
    .map((candidate) => {
      const sharedKeywords = [...normalizedKeywords(candidate)].filter(
        (keyword) => sourceKeywords.has(keyword),
      ).length;
      return { candidate, sharedKeywords };
    })
    .sort(
      (left, right) =>
        right.sharedKeywords - left.sharedKeywords ||
        new Date(right.candidate.date).getTime() -
          new Date(left.candidate.date).getTime(),
    )
    .slice(0, limit)
    .map(({ candidate }) => candidate);
}
